import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Package, Minus, Plus, User, Check, ShoppingCart, Search } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DistributorCreateOrderPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchClient, setSearchClient] = useState('');

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/store/products`),
      axios.get(`${API}/users/clients`),
    ]).then(([prodRes, cliRes]) => {
      setProducts(prodRes.data);
      setClients(cliRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(searchClient.toLowerCase()) ||
    c.email.toLowerCase().includes(searchClient.toLowerCase())
  );

  const handleSubmit = async () => {
    if (!selectedClient || !selectedProduct) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/distributor/recommend-product`, {
        client_id: selectedClient.id,
        product_id: selectedProduct.id,
        quantity,
      });
      toast.success(`Orden de ${selectedProduct.name} enviada a ${selectedClient.name}`);
      navigate('/distributor/supplements');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear la orden');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-orange-100 border-t-orange-500 animate-spin" />
      </div>
    );
  }

  const STEP_LABELS = { 1: 'Seleccionar producto', 2: 'Definir cantidad', 3: 'Elegir cliente' };
  const TYPE_LABELS = { semanal: '7 dias', mensual: '28 dias' };

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in pb-24" data-testid="create-order-page">
      <button
        onClick={() => step === 1 ? navigate('/distributor/supplements') : setStep(step - 1)}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        data-testid="back-btn"
      >
        <ArrowLeft size={16} /> {step === 1 ? 'Suplementos' : 'Paso anterior'}
      </button>

      {/* Progress */}
      <div className="flex items-center gap-2">
        {[1, 2, 3].map(s => (
          <div key={s} className="flex-1">
            <div className={`h-1.5 rounded-full transition-all duration-500 ${s <= step ? 'bg-orange-500' : 'bg-gray-200'}`} />
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 font-medium">
        Paso {step} de 3 — {STEP_LABELS[step]}
      </p>

      {/* STEP 1: Select virtual product */}
      {step === 1 && (
        <div className="space-y-4">
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Seleccionar Producto
          </h1>
          <p className="text-sm text-gray-400">Elige el kit de suplementos para tu cliente</p>
          {products.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100/50 p-10 text-center shadow-sm">
              <Package size={32} className="text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-400">No hay productos virtuales disponibles</p>
            </div>
          ) : (
            <div className="space-y-3">
              {products.map(p => {
                const isSelected = selectedProduct?.id === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setSelectedProduct(isSelected ? null : p)}
                    className={`w-full text-left bg-white rounded-2xl border p-4 transition-all duration-200 hover:shadow-md ${isSelected ? 'border-orange-400 ring-2 ring-orange-100 shadow-md' : 'border-gray-100/50 shadow-sm'}`}
                    data-testid={`product-select-${p.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-orange-500' : 'bg-gradient-to-br from-indigo-50 to-violet-50'}`}>
                        {isSelected ? <Check size={20} className="text-white" /> : <Package size={20} className="text-indigo-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 text-sm">{p.name}</p>
                        <span className="inline-block text-[10px] font-bold uppercase tracking-wide mt-0.5 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">
                          {TYPE_LABELS[p.type] || p.type}
                        </span>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-black text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                          RD${p.sale_price?.toLocaleString()}
                        </p>
                        <p className="text-[10px] text-gray-400">por unidad</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* STEP 2: Set quantity */}
      {step === 2 && selectedProduct && (
        <div className="space-y-4">
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Definir Cantidad
          </h1>
          <p className="text-sm text-gray-400">Ajusta la cantidad del producto seleccionado</p>
          <div className="bg-white rounded-2xl border border-gray-100/50 p-5 shadow-sm" data-testid="qty-product-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 flex items-center justify-center flex-shrink-0">
                <Package size={20} className="text-indigo-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-gray-900">{selectedProduct.name}</p>
                <span className="inline-block text-[10px] font-bold uppercase tracking-wide mt-0.5 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">
                  {TYPE_LABELS[selectedProduct.type] || selectedProduct.type}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between bg-gray-50 rounded-2xl p-4">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Cantidad</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:border-orange-300 hover:text-orange-500 transition-colors"
                  data-testid="qty-minus"
                >
                  <Minus size={16} />
                </button>
                <span className="text-2xl font-bold text-gray-900 w-10 text-center" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="qty-value">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(q => q + 1)}
                  className="w-10 h-10 rounded-xl bg-orange-500 hover:bg-orange-600 flex items-center justify-center text-white transition-colors"
                  data-testid="qty-plus"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
              <span className="text-xs text-gray-400 font-semibold uppercase">Total estimado</span>
              <p className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                RD${(selectedProduct.sale_price * quantity).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* STEP 3: Choose client */}
      {step === 3 && (
        <div className="space-y-4">
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Elegir Cliente
          </h1>
          <p className="text-sm text-gray-400">Selecciona el cliente que recibira la recomendacion</p>

          {/* Order Summary */}
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-4 border border-orange-100/50">
            <p className="text-xs font-bold text-orange-600 uppercase tracking-wider mb-2">Resumen</p>
            <div className="flex items-center justify-between py-1">
              <span className="text-xs text-gray-700">{selectedProduct?.name}</span>
              <span className="text-xs font-bold text-gray-900">x{quantity}</span>
            </div>
            <div className="border-t border-orange-200/50 mt-2 pt-2 flex items-center justify-between">
              <span className="text-xs text-gray-500">Total</span>
              <span className="text-sm font-black text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                RD${((selectedProduct?.sale_price || 0) * quantity).toLocaleString()}
              </span>
            </div>
          </div>

          {/* Client search */}
          {clients.length > 3 && (
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Buscar cliente..."
                value={searchClient}
                onChange={(e) => setSearchClient(e.target.value)}
                className="pl-11 bg-white border-gray-100 focus:border-orange-400 focus:ring-2 focus:ring-orange-500/20 rounded-xl h-11 shadow-sm"
                data-testid="search-clients"
              />
            </div>
          )}

          {filteredClients.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100/50 p-10 text-center shadow-sm">
              <p className="text-sm text-gray-400">
                {searchClient ? 'Sin resultados' : 'No tienes clientes asignados'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredClients.map(c => {
                const selected = selectedClient?.id === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => setSelectedClient(c)}
                    className={`w-full text-left bg-white rounded-2xl border p-4 transition-all duration-200 hover:shadow-md ${selected ? 'border-orange-400 ring-2 ring-orange-100 shadow-md' : 'border-gray-100/50 shadow-sm'}`}
                    data-testid={`client-select-${c.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${selected ? 'bg-orange-500' : 'bg-gray-100'}`}>
                        {selected ? <Check size={18} className="text-white" /> : <User size={18} className="text-gray-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 text-sm">{c.name}</p>
                        <p className="text-xs text-gray-400">{c.email}</p>
                      </div>
                      {c.display_id && <span className="text-[10px] font-mono text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">{c.display_id}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Fixed Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-gray-200 px-4 py-3 z-50">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          {selectedProduct && (
            <div className="flex items-center gap-1.5 bg-orange-50 text-orange-600 rounded-xl px-3 py-2">
              <ShoppingCart size={14} />
              <span className="text-xs font-bold">{selectedProduct.name}</span>
            </div>
          )}
          <div className="flex-1" />
          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 && !selectedProduct}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-40 transition-all duration-300 hover:-translate-y-0.5"
              style={{ background: selectedProduct ? '#F97316' : '#d1d5db' }}
              data-testid="next-step-btn"
            >
              Siguiente <ArrowRight size={14} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!selectedClient || submitting}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-40 transition-all duration-300 hover:-translate-y-0.5"
              style={{ background: selectedClient ? '#467322' : '#d1d5db' }}
              data-testid="submit-order-btn"
            >
              {submitting ? 'Enviando...' : 'Enviar Orden'} <Check size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DistributorCreateOrderPage;
